import React, { useEffect, useState, useCallback } from 'react';
import { Bell, CheckCheck, Inbox, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { ACK_REQUIRED_TYPES } from '../lib/notifAck';

const typeIcon = {
  price_approved: '✓',
  price_rejected: '×',
  price_flagged: '!',
  price_pending: '…',
  user_registered: '+',
  welcome: '★',
  price_push_24h: '24h',
  price_push_2h: '2h',
  price_push_eff: '!',
  price_confirmed: '✓',
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

/**
 * Feed de notificaciones del usuario (movido desde MO-P01).
 * Ahora vive dentro de MO-P06 "Cambios de precio", debajo de los pushes.
 */
const NotificationsFeed = () => {
  const { user } = useAuth();
  // Acuse explícito requerido para agent y territory_manager (paridad de roles).
  const requiresAck = user?.role === 'agent' || user?.role === 'territory_manager';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackingId, setAckingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications', { params: { limit: 100 } });
      setItems(r.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30 * 1000); return () => clearInterval(t); }, [load]);

  const markRead = async (id) => {
    setItems(items.map(n => n.id === id ? { ...n, read: true } : n));
    try { await api.post(`/notifications/${id}/read`); } catch { /* noop */ }
  };

  const markAllRead = async () => {
    setItems(items.map(n => ({ ...n, read: true })));
    try { await api.post('/notifications/read-all'); } catch { /* noop */ }
  };

  const acknowledge = async (id) => {
    setAckingId(id);
    setItems(items.map(n => n.id === id ? { ...n, read: true, acknowledged_at: new Date().toISOString() } : n));
    try {
      await api.post(`/notifications/${id}/acknowledge`);
      toast.success('Confirmado');
    } catch {
      toast.error('Error al confirmar');
    } finally { setAckingId(null); }
  };

  const acknowledgeAll = async () => {
    const now = new Date().toISOString();
    setItems(items.map(n => ACK_REQUIRED_TYPES.has(n.type) && !n.acknowledged_at ? { ...n, read: true, acknowledged_at: now } : n));
    try {
      const r = await api.post('/notifications/acknowledge-all');
      toast.success(`${r.data.acknowledged || 0} notificaciones confirmadas`);
    } catch {
      toast.error('Error al confirmar todas');
    }
  };

  const unread = items.filter(n => !n.read).length;
  const ackPending = items.filter(n => ACK_REQUIRED_TYPES.has(n.type) && !n.acknowledged_at).length;

  // Items that should be visible: not read (or, for ack-required types in agent/TM
  // role, not yet acknowledged). Once user marks read / confirms, the row disappears.
  const pendingItems = items.filter(n => {
    if (requiresAck && ACK_REQUIRED_TYPES.has(n.type)) return !n.acknowledged_at;
    return !n.read;
  });
  const historyItems = items.filter(n => {
    if (requiresAck && ACK_REQUIRED_TYPES.has(n.type)) return !!n.acknowledged_at;
    return !!n.read;
  }).slice(0, 20);
  const visibleItems = showHistory ? historyItems : pendingItems;

  return (
    <section className="space-y-3" data-testid="notifications-feed">
      <header className="flex items-end justify-between gap-3 pt-2">
        <div>
          <h2 className="font-serif text-[22px] leading-tight tracking-tight inline-flex items-center gap-2">
            <Bell size={16} strokeWidth={1.6}/> Alertas
          </h2>
          <p className="text-[11.5px] text-[#5a5a5a]">{unread > 0 ? `${unread} sin leer` : 'Todo al día'}</p>
        </div>
        {unread > 0 && (
          <Button onClick={markAllRead} variant="outline" className="h-8 rounded-full text-[11.5px] px-3" data-testid="btn-mark-all-read">
            <CheckCheck size={12} className="mr-1"/> Marcar todas
          </Button>
        )}
      </header>

      {/* Agent banner */}
      {requiresAck && ackPending > 0 && (
        <div className="flex items-center justify-between gap-2 bg-[#fef3c7] border border-[#fde68a] rounded-xl p-3" data-testid="agent-ack-banner">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck size={15} className="text-[#92400e] shrink-0"/>
            <p className="text-[12px] text-[#92400e]"><strong>{ackPending}</strong> cambio{ackPending > 1 ? 's' : ''} de precio pendiente{ackPending > 1 ? 's' : ''} de confirmar visto</p>
          </div>
          <Button
            onClick={acknowledgeAll}
            className="h-8 rounded-full bg-[#92400e] hover:bg-[#7c2d12] text-white text-[11.5px] px-3 shrink-0"
            data-testid="agent-ack-all"
          >
            Confirmar todos
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : visibleItems.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center">
          <Inbox size={24} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
          <p className="text-[12.5px] text-[#8a8579]">{items.length === 0 ? 'Sin notificaciones todavía.' : 'Todo al día.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleItems.map(n => {
            const goPushes = n.type === 'price_push_24h' || n.type === 'price_push_2h' || n.type === 'price_push_eff' || n.type === 'price_pending';
            // Alertas con estación asociada (tracking incompleto, diferenciales, sin captura…)
            // navegan al Detalle de estación con sus precios/trackings/markers.
            const stationId = !goPushes ? n.data?.station_id : null;
            const needsAck = requiresAck && ACK_REQUIRED_TYPES.has(n.type) && !n.acknowledged_at;
            const Wrapper = (goPushes || stationId) ? Link : 'button';
            const wrapperProps = goPushes
              ? { to: '/app/pushes', onClick: () => { if (!n.read) markRead(n.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }, 'data-testid': `notif-go-pushes-${n.id}` }
              : stationId
                ? { to: `/app/station/${stationId}`, onClick: () => !n.read && markRead(n.id), 'data-testid': `notif-go-station-${n.id}` }
                : { onClick: () => !n.read && markRead(n.id), type: 'button' };
            const cardBg = !n.read ? 'bg-[#fffbeb] border-[#fde68a]' : 'bg-white border-[#e8e5de]';
            return (
              <div
                key={n.id}
                className={`rounded-xl border overflow-hidden ${cardBg}`}
                data-testid={`notif-${n.id}`}
              >
                <Wrapper
                  {...wrapperProps}
                  className={`w-full text-left px-4 py-3 transition flex items-start gap-3 ${!n.read ? 'hover:bg-[#fef3c7]' : 'hover:bg-[#f8f6f1]'}`}
                >
                  <span className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center text-[12px] shrink-0 font-medium ${
                    n.type === 'price_approved' || n.type === 'price_confirmed' ? 'bg-[#15803d] text-white' :
                    n.type === 'price_rejected' ? 'bg-[#fee2e2] text-[#991b1b]' :
                    n.type === 'price_flagged' || n.type?.startsWith('price_push') ? 'bg-[#fef3c7] text-[#92400e]' :
                    'bg-[#1c1c1c] text-[#f8f6f1]'
                  }`}>
                    {typeIcon[n.type] || '·'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[14px] leading-tight ${!n.read ? 'font-medium' : 'text-[#3a3a3a]'}`}>{n.title}</p>
                    {n.body && <p className="text-[12.5px] text-[#5a5a5a] mt-0.5 leading-snug">{n.body}</p>}
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <p className="text-[11px] text-[#8a8579]">hace {formatTime(n.created_at)}</p>
                      {goPushes && <span className="text-[10.5px] text-[#92400e] font-medium inline-flex items-center gap-0.5">Ir a confirmar <ArrowRight size={10}/></span>}
                      {stationId && <span className="text-[10.5px] text-[#0f1d6f] font-medium inline-flex items-center gap-0.5">Ver estación y tracking <ArrowRight size={10}/></span>}
                    </div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#b91c1c] mt-2 shrink-0"/>}
                </Wrapper>
                {needsAck && (
                  <div className="border-t border-[#fde68a] bg-[#fffbeb] px-4 py-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-[#92400e] inline-flex items-center gap-1.5">
                      <ShieldCheck size={12} strokeWidth={1.8}/> Requiere confirmación del agente
                    </span>
                    <Button
                      onClick={() => acknowledge(n.id)}
                      disabled={ackingId === n.id}
                      className="h-7 rounded-full bg-[#92400e] hover:bg-[#7c2d12] text-white text-[11px] px-3"
                      data-testid={`ack-${n.id}`}
                    >
                      {ackingId === n.id ? <Loader2 size={11} className="animate-spin"/> : <>Confirmar visto</>}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {historyItems.length > 0 && !loading && (
        <div className="pt-2 text-center">
          <button
            onClick={() => setShowHistory(s => !s)}
            className="text-[11.5px] text-[#5a5a5a] hover:text-[#0f1d6f] underline underline-offset-4 transition"
            data-testid="toggle-history"
          >
            {showHistory ? '← Volver a pendientes' : `Ver historial (${historyItems.length})`}
          </button>
        </div>
      )}
    </section>
  );
};

export default NotificationsFeed;
